import { createFileRoute } from "@tanstack/react-router";
import { IMAGES } from "@/lib/images";
import {
  GuideHero,
  GuideBody,
  GuideFaq,
  GuideCta,
  GuideRelated,
  GuideInlineCta,
  KeyStats,
  ProsCons,
  ResponsiveTable,
  Callout,
  buildGuideHead,
  type GuideMeta,
} from "@/components/GuideLayout";

const meta: GuideMeta = {
  category: "Jämförelse",
  title: "Solfilm eller persienner? Komplett jämförelse 2026",
  tldr:
    "Solfilm reflekterar bort värme innan den kommer in i rummet och kräver inget underhåll. Persienner blockerar både värme och ljus men kräver hantering, samlar damm och har kortare livslängd. För kontor och bostäder med stora glas är solfilm överlägset på värmereduktion per krona, medan persienner vinner på att kunna stängas helt för mörker. Många väljer faktiskt att kombinera båda.",
  readTimeMin: 8,
  heroImage: IMAGES.solfilmHero,
  path: "/guider/solfilm-vs-persienner",
  updated: "2026-06-16",
  faqs: [
    {
      q: "Tar solfilm bort all värme?",
      a: "Nej — 3M Prestige 70 reducerar solvärmen med 50 % (TSER). Vill du ha 100 % blockering behövs blackout-gardin eller utvändig solavskärmning. Men för att hålla rumstemperatur och bländning på en behaglig nivå räcker filmen i 9 av 10 fall.",
    },
    {
      q: "Varför skulle jag välja solfilm istället för persienner som jag redan har?",
      a: "Persienner stänger ute ljuset samtidigt som de blockerar värmen, och de behöver hanteras manuellt. Solfilm verkar dygnet runt utan åtgärd, sänker bländning utan att rummet blir mörkt, och frigör fönstret rent visuellt. För arkitektoniska glaspartier väljer många film just för att slippa persienner.",
    },
    {
      q: "Kan jag ha både film och persienner?",
      a: "Ja, och det är vanligt. Filmen sköter dagsdriften (värme, UV, bländning), persiennen används vid presentationer, eftermiddagssol eller för att mörka inför sömn. Tillsammans får man maximal flexibilitet.",
    },
    {
      q: "Vad blir billigast på 10 år?",
      a: "Solfilm. En film håller 12–15+ år utan underhåll, kvalitetspersienner behöver ofta bytas inom 5–8 år och kräver dammning + servicebesök på motoriserade system. För ett genomsnittligt kontor är film 30–50 % billigare i livscykelkostnad.",
    },
  ],
  related: [
    { to: "/guider/solfilm-kontor", label: "Solfilm för kontor — guide" },
    { to: "/guider/solfilm-villa", label: "Solfilm för villa & lägenhet" },
    { to: "/3m-prestige", label: "3M Prestige-serien — datablad" },
  ],
};

export const Route = createFileRoute("/guider/solfilm-vs-persienner")({
  head: () => buildGuideHead(meta),
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      <GuideHero meta={meta} />
      <GuideBody>
        <KeyStats
          items={[
            { value: "50 %", label: "Värme ut (film)", sub: "3M Prestige 70, alltid aktiv" },
            { value: "60 %", label: "Värme ut (persienn)", sub: "endast helt stängd" },
            { value: "15 år", label: "Livslängd film", sub: "12–15+ år" },
            { value: "5–8 år", label: "Livslängd persienn", sub: "motoriserad kortare" },
          ]}
        />

        <h2>Det är inte antingen-eller — men det är inte heller samma sak</h2>
        <p>
          Solfilm och persienner löser delvis samma problem (för mycket sol) men på helt
          olika sätt. Den ena verkar på glasets yta dygnet runt, den andra hänger framför
          glaset och stängs när du behöver det. Vilken som är bäst beror på vad du faktiskt
          vill ha kontroll över.
        </p>

        <GuideInlineCta />
        <h2>Sex parametrar — sida vid sida</h2>
        <ResponsiveTable>
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Solfilm (3M Prestige 70)</th>
              <th>Persienner (kvalitet)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Värmereduktion</strong></td>
              <td>50 % TSER, alltid aktiv</td>
              <td>Upp till 60 % när helt stängda</td>
            </tr>
            <tr>
              <td><strong>Dagsljus inomhus</strong></td>
              <td>Behålls (68 % VLT)</td>
              <td>Reduceras kraftigt när stängda</td>
            </tr>
            <tr>
              <td><strong>UV-skydd</strong></td>
              <td>99 % blockering, alltid aktiv</td>
              <td>Endast när helt stängda</td>
            </tr>
            <tr>
              <td><strong>Underhåll</strong></td>
              <td>Rengöring med vatten 1–2 gånger/år</td>
              <td>Damning, motorservice, byte av lameller</td>
            </tr>
            <tr>
              <td><strong>Livslängd</strong></td>
              <td>12–15+ år (10 års garanti)</td>
              <td>Typiskt 5–8 år, motoriserade kortare</td>
            </tr>
            <tr>
              <td><strong>Visuellt</strong></td>
              <td>Nästan osynlig på glaset</td>
              <td>Dominerar fönsterpartiet</td>
            </tr>
          </tbody>
        </ResponsiveTable>

        <h2>Solfilm vs persienner — för- och nackdelar</h2>
        <ProsCons
          prosTitle="Solfilm — fördelar"
          consTitle="Solfilm — begränsningar"
          pros={[
            "Verkar dygnet runt utan handpåläggning",
            "Behåller dagsljus och fri sikt",
            "99 % UV-skydd för möbler och parkett",
            "Kombineras med säkerhets- och splitterskydd",
            "Inget underhåll utöver normal fönsterputs",
          ]}
          cons={[
            "Mörklägger inte rummet helt",
            "Dämpar inte ljud / akustik",
            "Kan inte justeras efter dagens behov",
          ]}
        />
        <ProsCons
          prosTitle="Persienner — fördelar"
          consTitle="Persienner — begränsningar"
          pros={[
            "Total mörkläggning vid behov",
            "Kan justeras efter dagsljus",
            "Tyngre tygpersienner dämpar buller",
          ]}
          cons={[
            "Blockerar ljus när stängd",
            "Kräver damning och motorservice",
            "Dominerar visuellt fönsterpartiet",
            "Skyddar inte mot UV när öppna",
          ]}
        />


        <GuideInlineCta />
        <h2>När persienner är bättre</h2>
        <ul>
          <li><strong>Mörkläggning för sömn eller presentation</strong> — film blockerar inte ljus, persienner gör det.</li>
          <li><strong>Variabelt skydd</strong> — vill du ha fullt ljus halva tiden och fullt skydd halva tiden vinner persienner.</li>
          <li><strong>Akustik</strong> — tyngre tygpersienner dämpar buller; film påverkar inte ljud alls.</li>
        </ul>

        <h2>När solfilm är överlägsen</h2>
        <ul>
          <li><strong>Arkitektoniska glaspartier</strong> — där persienner skulle förstöra utseendet.</li>
          <li><strong>Hög sol och bländning</strong> — film verkar utan att du behöver justera.</li>
          <li><strong>UV-skydd för möbler och konst</strong> — film verkar dygnet runt, persienner gör det inte.</li>
          <li><strong>Lågt underhåll</strong> — kontor med många glas, hotell, butiker.</li>
          <li><strong>Säkerhetsfilm-kombination</strong> — då får du splitter- och inbrottsskydd på köpet.</li>
        </ul>

        <Callout variant="tip" title="Vanligaste valet på kommersiella objekt">
          <p>
            Film som baslösning på alla fönster + persienner endast i mötesrum, soffan i lounge
            eller sovrum (hotell). Du får maximal komfort och lägst driftkostnad utan att tappa
            mörkläggningsmöjligheten där den faktiskt behövs.
          </p>
        </Callout>
      </GuideBody>
      <GuideFaq items={meta.faqs ?? []} />
      <GuideCta />
      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
